/**
 * Reine Logik des Abschlusses (docs/sifa-ausbau2-plan.md §0.3, §3.3):
 * Bestaetigung vor der Unterschrift, Datenschutz-Fassung und Test-Kontingent.
 *
 * Die Bestaetigung geht WOERTLICH so an die Datenbank, wie sie angezeigt
 * wurde — deshalb baut sie genau eine Funktion (baueBestaetigung), und die
 * Seite zeigt deren `text` an, statt den Satz ein zweites Mal zu bilden.
 */
import type { Bestaetigung, DatenschutzText } from '../../../services/sifa/types';
import { BESTAETIGUNG_TEXT } from '../../../services/sifa/datenschutzMuster';
import { datenschutzOderMuster } from './datenschutzSeiteLogic';

/** Zweites Pflicht-Haekchen; „Datenschutzhinweise" ist darin der Link. */
export const DATENSCHUTZ_HAEKCHEN = {
  vor: 'Ich habe die ',
  link: 'Datenschutzhinweise',
  nach: ' zur Kenntnis genommen.',
} as const;

/** Beide Pflicht-Haekchen gesetzt — erst dann gibt es Unterschrift und QR-Bruecke. */
export const abschlussFrei = (h: { bestaetigt: boolean; datenschutzGelesen: boolean }): boolean =>
  h.bestaetigt === true && h.datenschutzGelesen === true;

/**
 * Die Bestaetigung fuer sifa_teilnahme_abschliessen_v2. Ohne eigene Fassung
 * der Firma zaehlt das Muster: id null, Fassung 0 (Vertrag §3.1).
 */
export const baueBestaetigung = (titel: string, datenschutz: DatenschutzText | null | undefined): Bestaetigung => {
  const d = datenschutzOderMuster(datenschutz);
  return {
    bestaetigt: true,
    text: BESTAETIGUNG_TEXT(titel),
    datenschutzId: d.id || null,
    datenschutzFassung: d.id ? d.fassung : 0,
  };
};

/**
 * Fassung der Datenschutzhinweise, wie der Hook sie kennt: `bereit` mit null
 * heisst „keine eigene Fassung" (→ Muster), `laedt`/`fehler` heisst „noch
 * nicht bekannt".
 */
export type DatenschutzStand = { stand: 'laedt' | 'fehler'; text: null } | { stand: 'bereit'; text: DatenschutzText | null };

/** Ergebnis des Neuladens unmittelbar vor dem Abschluss. */
export type DatenschutzNeu = { ok: true; text: DatenschutzText | null } | { ok: false };

export const DATENSCHUTZ_AKTUALISIERT = 'Die Datenschutzhinweise wurden aktualisiert — bitte erneut lesen und bestätigen.';

/**
 * Entscheidet unmittelbar vor dem Abschluss, ob die bestaetigte Fassung noch
 * gilt. Der Link oeffnet immer die NEUESTE Fassung; wurde seit dem Einstieg
 * eine neue veroeffentlicht, hat der Teilnehmer womoeglich die neue gelesen,
 * die Seite aber die alte im Kopf — dann nicht abschliessen, sondern neu
 * bestaetigen lassen (`weiter: false`).
 *
 * - Neuladen gescheitert: weiter mit der zuletzt geladenen Fassung (fail-open).
 * - Verglichen wird die id (Fassungen werden nie geaendert, nur neu
 *   angelegt); Muster gegen Muster ist gleich.
 * - Scheiterte schon das Laden beim Einstieg, galt bis hierher das Muster —
 *   bringt das Nachladen jetzt erstmals eine EIGENE Fassung, ist das eine
 *   andere als die angezeigte und wird neu bestaetigt. Weiter nur, wenn das
 *   Nachgeladene dem entspricht, was galt.
 */
export const datenschutzVorAbschluss = (
  bisher: DatenschutzStand,
  neu: DatenschutzNeu,
): { weiter: boolean; text: DatenschutzText | null } => {
  const bisherText = bisher.stand === 'bereit' ? bisher.text : null;
  if (!neu.ok) return { weiter: true, text: bisherText };
  const gleich = datenschutzOderMuster(bisherText).id === datenschutzOderMuster(neu.text).id;
  return { weiter: gleich, text: neu.text };
};

/** „noch 3 Testauswertungen mit diesem Link"; ohne Angabe leer. */
export const auswertungenRestText = (rest: number | null | undefined): string => {
  if (rest === null || rest === undefined || !Number.isFinite(rest)) return '';
  const n = Math.max(0, Math.floor(rest));
  if (n === 0) return 'Keine weitere Testauswertung mit diesem Link.';
  return n === 1 ? 'Noch 1 Testauswertung mit diesem Link.' : `Noch ${n} Testauswertungen mit diesem Link.`;
};

/**
 * Kontingent des Test-Links erschoepft: der Dienst wirft dafuer
 * `TestKontingentErschoepftError` (SQLSTATE 54000). Erkannt am Namen ODER am
 * Code — der Hook prueft zusaetzlich `instanceof`; dies hier ist der Rueckfall
 * fuer einen Fehler, der anders verpackt ankommt.
 */
export const istKontingentErschoepft = (e: unknown): boolean => {
  if (!e || typeof e !== 'object') return false;
  const f = e as { name?: unknown; code?: unknown };
  return f.name === 'TestKontingentErschoepftError' || f.code === '54000';
};

/** Erreichte Punkte in Prozent, ganzzahlig; ohne Punkte 0. */
export const erreichtProzent = (punkte: number, punkteMax: number): number =>
  punkteMax > 0 ? Math.round((Math.max(0, punkte) / punkteMax) * 100) : 0;

const BERLIN = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Ablauf des Test-Links als „08.10.2026, 14:30 Uhr" (Berliner Zeit, wie die
 * App ueberall rechnet). Leer bei fehlendem oder kaputtem Zeitstempel.
 */
export const gueltigBisText = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const teile = Object.fromEntries(BERLIN.formatToParts(d).map((p) => [p.type, p.value]));
  return `${teile.day}.${teile.month}.${teile.year}, ${teile.hour}:${teile.minute} Uhr`;
};
